<template>
  <!--
    ===================================================================
    NEW LAYOUT (newLayout=true) — FANS LEADERBOARD feature band.
    ===================================================================
    Figma "Monaco" frame (node 18681:46917 desktop / 18681:47496 mobile):

      • A band filled with the channel brand colour and a soft diagonal
        texture (`.leaderboard-band`). The fill comes from the `--fill-brand`
        token + a token-driven highlight gradient, so it adapts to each
        channel's theme colour (never hardcoded red). The band is CENTERED:
        it stays inside the normal max-width-constrained page content column
        (same as the QUESTS / contests section above it) and does NOT bleed to
        the viewport / page-region edges. Its own horizontal gutter (`px-5`)
        matches the sibling sections so the panel aligns with them in both
        chat-closed and chat-open layouts.

      • A white "FANS LEADERBOARD" title inside the band.

      • A LIGHT inner panel (`surface-secondary`) holding the prize-banner
        header zone (fan badge + prize headline + days-left countdown +
        Weekly/Monthly tabs + Full Leaderboard) ON TOP, then the ranked
        rows on a light `surface` background.

    The prize-banner header renders INDEPENDENT of row data: the fan badge,
    tabs, host controls and Full Leaderboard always render; the prize
    headline/countdown show when a prize is set (ChannelMonthlyPrize) and a
    neutral pre-prize line shows otherwise.
  -->
  <template v-if="newLayout">
    <MvpLeaderboardSkeleton v-if="loading" class="mx-5" />

    <div
      v-else
      class="leaderboard-band relative mx-auto w-full overflow-hidden"
    >
      <div class="leaderboard-band-texture" aria-hidden="true" />

      <div class="relative z-10 flex w-full flex-col gap-6 px-5">
        <h2
          class="text-xl lg:text-[27px] font-serif uppercase leading-tight text-text-on-brand"
        >
          {{ $t("channel.mvpLeaderboard.bannerTitle") }}
        </h2>

        <TabsRoot
          v-model="activeTab"
          :default-value="LeaderboardLength.Weekly"
          class="flex w-full flex-col overflow-hidden rounded-[20px] bg-surface-secondary"
        >
          <!-- Prize-banner header zone -->
          <div
            class="flex flex-col gap-3 bg-black/[0.06] p-4 lg:flex-row lg:items-center lg:justify-between"
          >
            <div class="flex flex-1 flex-row items-center gap-3">
              <div
                class="flex shrink-0 flex-col items-center justify-between gap-1 self-stretch rounded-full bg-fill-brand px-1.5 pb-2 pt-1.5"
              >
                <img
                  :src="communityOrChannelLogo"
                  :alt="$t('channel.mvpLeaderboard.title')"
                  class="size-8 min-w-8 rounded-full border-2 border-white/70"
                />
                <Icon
                  icon="mdi:people"
                  class="size-4 text-text-on-brand"
                  aria-hidden="true"
                />
              </div>

              <div class="flex min-w-0 flex-1 flex-col justify-center">
                <!--
                  ChannelMonthlyPrize always mounts (it `v-show`s its own block
                  when a prize exists) and reports presence via
                  `update:prizePresent`; the pre-prize fallback below renders in
                  its place when no prize is set so the header never looks empty.
                -->
                <ChannelMonthlyPrize
                  mini
                  new-layout
                  size="sm"
                  :leaderboard-length="activeTab"
                  @update:prize-present="hasPrize = $event"
                />
                <div v-if="!hasPrize" class="flex flex-col gap-0.5">
                  <span
                    class="text-base font-semibold font-sans capitalize leading-tight text-text"
                  >
                    {{ $t("channel.mvpLeaderboard.bannerTitle") }}
                  </span>
                  <span
                    class="text-sm font-medium font-sans leading-tight text-text-secondary"
                  >
                    {{ $t("channel.mvpLeaderboard.bannerSubtitle") }}
                  </span>
                </div>
              </div>
            </div>

            <div
              class="flex w-full flex-col items-stretch gap-3 sm:flex-row sm:items-center lg:w-fit"
            >
              <TabsList
                :aria-label="$t('channel.mvpLeaderboard.periodTabs')"
                class="relative flex h-10 w-full rounded-full bg-surface px-1 text-base font-serif text-text lg:w-fit"
              >
                <TabsIndicator
                  class="absolute bottom-0 left-0 top-0 h-full w-[--reka-tabs-indicator-size] translate-x-[--reka-tabs-indicator-position] py-1 transition-[width,transform] duration-300"
                >
                  <div class="h-full rounded-full bg-fill-brand" />
                </TabsIndicator>

                <TabsTrigger
                  class="relative z-10 line-clamp-1 h-full w-1/2 cursor-pointer select-none whitespace-nowrap px-4 py-3 text-sm uppercase leading-none outline-none data-[state=active]:text-text-on-brand lg:w-fit"
                  :value="LeaderboardLength.Weekly"
                >
                  {{ $t("channel.mvpLeaderboard.tabs.weekly") }}
                </TabsTrigger>

                <TabsTrigger
                  class="relative z-10 line-clamp-1 h-full w-1/2 cursor-pointer select-none whitespace-nowrap px-4 py-3 text-sm uppercase leading-none outline-none data-[state=active]:text-text-on-brand lg:w-fit"
                  :value="LeaderboardLength.Monthly"
                >
                  {{ $t("channel.mvpLeaderboard.tabs.monthly") }}
                </TabsTrigger>
              </TabsList>

              <button
                type="button"
                class="flex h-10 shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-surface px-4 text-sm font-semibold font-sans leading-tight text-text transition-colors hover:bg-surface-hover"
                @click="isFullLeaderboardModalOpen = true"
              >
                {{ $t("channel.mvpLeaderboard.fullLeaderboard") }}
              </button>

              <ManageLeaderboardPopover
                v-if="
                  userStore.isAuthenticated &&
                  channelStore.permissions.manageChannel
                "
              />
            </div>
          </div>

          <!--
            Ranked rows on the light panel. The zone keeps the panel's
            `surface-secondary` (white) background so the ContestLeaderboard
            "YOU" row — which paints itself `bg-surface` (#f0f0f0) — stays
            visibly distinct, matching the Figma highlighted self-row. We do
            NOT touch ContestLeaderboard itself (shared across the modal /
            simulfest / legacy surfaces).
          -->
          <div class="w-full px-3 py-4">
            <ContestLeaderboard
              :loading="false"
              :ranking="ranking"
              :leaderboard-length="activeTab"
              :loading-text="$t('channel.mvpLeaderboard.loading')"
              :empty-text="$t('channel.mvpLeaderboard.empty')"
            />
          </div>

          <div
            v-if="
              userStore.isAuthenticated &&
              channelStore.hasSuperfanProduct &&
              !channelStore.isSuperfan &&
              !channelStore.isHost
            "
            class="flex flex-col items-stretch justify-center gap-3 p-4 md:flex-row md:items-center"
          >
            <div class="flex flex-1 items-center gap-2">
              <CoinPile class="h-16 w-12 md:h-11 md:w-8" />
              <div class="flex flex-col">
                <h3 class="text-lg font-serif text-text">
                  {{ $t("channel.mvp.multiplier.title") }}
                </h3>
                <p class="text-sm text-text-secondary">
                  {{ $t("channel.mvp.multiplier.description") }}
                </p>
              </div>
            </div>

            <UnlockPointsMultiplierButton />
          </div>
        </TabsRoot>
      </div>
    </div>
  </template>

  <!--
    ===================================================================
    LEGACY LAYOUT (newLayout=false, default) — byte-for-byte unchanged.
    ===================================================================
  -->
  <ChannelSection v-else>
    <MvpLeaderboardSkeleton v-if="loading" />

    <div v-else class="flex flex-col items-center mx-5 rounded-[24px]">
      <TabsRoot
        v-model="activeTab"
        :default-value="LeaderboardLength.Weekly"
        class="flex flex-col w-full"
        :class="[
          'bg-surface',
          { 'rounded-3xl': !channelStore.isAsMonacoFanToken },
        ]"
      >
        <div
          class="flex-col justify-start items-center inline-flex"
          :class="[
            'bg-surface border-[3px] border-border-brand',
            { 'rounded-3xl': !channelStore.isAsMonacoFanToken },
          ]"
        >
          <div
            class="p-4 self-stretch justify-between items-start gap-3 flex flex-col lg:flex-row"
            :class="[
              'bg-surface',
              { 'rounded-t-3xl': !channelStore.isAsMonacoFanToken },
            ]"
          >
            <div class="flex flex-row gap-3">
              <div
                class="self-stretch p-1.5 pb-2 rounded-full flex-col justify-between items-center inline-flex bg-fill-brand-secondary"
              >
                <img
                  :src="communityOrChannelLogo"
                  :alt="$t('channel.mvpLeaderboard.title')"
                  class="size-8 min-w-8 rounded-full border-2 border-brand-primary"
                />
                <div class="flex-col justify-start items-center flex">
                  <Icon icon="mdi:people" class="w-4 h-4 text-[#d9d9d9]" />
                </div>
              </div>

              <ChannelMonthlyPrize
                mini
                size="sm"
                :leaderboard-length="activeTab"
              />
            </div>

            <div class="flex gap-3 w-full lg:w-fit">
              <div
                class="self-stretch justify-end items-start inline-flex w-full lg:w-fit"
              >
                <TabsList
                  aria-label="profile-tabs"
                  class="relative px-1 flex rounded-3xl text-base font-serif w-full lg:w-fit h-10 bg-surface text-basics-primary"
                >
                  <TabsIndicator
                    class="absolute py-1 left-0 h-full bottom-0 top-0 w-[--reka-tabs-indicator-size] translate-x-[--reka-tabs-indicator-position] transition-[width,transform] duration-300"
                  >
                    <div class="h-full rounded-2xl bg-fill-brand" />
                  </TabsIndicator>

                  <TabsTrigger
                    class="uppercase px-4 text-sm h-full relative z-10 py-3 line-clamp-1 leading-none select-none outline-none cursor-pointer w-1/2 lg:w-fit whitespace-nowrap"
                    :value="LeaderboardLength.Weekly"
                  >
                    {{ $t("channel.mvpLeaderboard.tabs.weekly") }}
                  </TabsTrigger>

                  <TabsTrigger
                    class="uppercase px-4 text-sm h-full relative z-10 py-3 line-clamp-1 leading-none select-none outline-none cursor-pointer w-1/2 lg:w-fit whitespace-nowrap"
                    :value="LeaderboardLength.Monthly"
                  >
                    {{ $t("channel.mvpLeaderboard.tabs.monthly") }}
                  </TabsTrigger>
                </TabsList>
              </div>
              <ManageLeaderboardPopover
                v-if="
                  userStore.isAuthenticated &&
                  channelStore.permissions.manageChannel
                "
              />
            </div>
          </div>

          <div class="w-full">
            <ContestLeaderboard
              :loading="false"
              :ranking="ranking"
              :leaderboard-length="activeTab"
              :loading-text="$t('channel.mvpLeaderboard.loading')"
              :empty-text="$t('channel.mvpLeaderboard.empty')"
            />
          </div>

          <div
            class="self-stretch p-4 pt-0 flex-col justify-start items-start gap-2.5 flex"
          >
            <button
              class="w-full flex justify-center items-center h-10 px-4 py-1 rounded-full border-t text-center text-sm font-medium font-sans leading-tight text-white bg-fill-brand border-white/40"
              @click="isFullLeaderboardModalOpen = true"
            >
              {{ $t("channel.mvpLeaderboard.fullLeaderboard") }}
            </button>
          </div>
        </div>

        <div
          v-if="
            userStore.isAuthenticated &&
            channelStore.hasSuperfanProduct &&
            !channelStore.isSuperfan &&
            !channelStore.isHost
          "
          class="flex flex-col md:flex-row items-stretch md:items-center justify-center gap-3 p-4"
        >
          <div class="flex-1 flex items-center gap-2">
            <CoinPile class="w-12 h-16 md:h-11 md:w-8" />
            <div class="flex flex-col">
              <h3 class="text-lg text-text-on-brand font-serif">
                {{ $t("channel.mvp.multiplier.title") }}
              </h3>
              <p class="text-text text-sm">
                {{ $t("channel.mvp.multiplier.description") }}
              </p>
            </div>
          </div>

          <UnlockPointsMultiplierButton />
        </div>
      </TabsRoot>
    </div>
  </ChannelSection>

  <LeaderboardModal
    :is-open="isFullLeaderboardModalOpen"
    @close="isFullLeaderboardModalOpen = false"
  />
</template>

<script setup lang="ts">
import { graphql as gql } from "@/generated";
import { useChannelStore } from "@/stores/channel.store";
import { useLoyaltyNotifications } from "@/features/channel/services/use-loyalty-notifications";
import { computed, ref, watch } from "vue";
import { TabsIndicator, TabsList, TabsRoot, TabsTrigger } from "reka-ui";
import ChannelMonthlyPrize from "@/components/channel/channel-monthly-prize.vue";
import { Icon } from "@iconify/vue";
import ContestLeaderboard, {
  ContestLeaderboardUserFragment,
} from "@/components/contest/contest-leaderboard.vue";
import ChannelSection from "@/components/channel/channel-section.vue";
import LeaderboardModal from "@/components/leaderboard/leaderboard-modal.vue";
import { LeaderboardLength } from "@f3/shared/leaderboard-types";
import { AppThemeKind, useThemeStore } from "@/stores/theme.store";
import CoinPile from "@/components/ui/icon/coin-pile.vue";
import UnlockPointsMultiplierButton from "@/components/ui/button/unlock-points-multiplier-button.vue";
import { useUserStore } from "@/stores/user.store";
import ManageLeaderboardPopover from "@/components/leaderboard/manage-leaderboard-popover.vue";
import { useQuery } from "@urql/vue";
import MvpLeaderboardSkeleton from "@/components/ui/skeleton/mvp-leaderboard-skeleton.vue";
import type { ResultOf } from "@graphql-typed-document-node/core";

type ContestLeaderboardUser = ResultOf<typeof ContestLeaderboardUserFragment>;

withDefaults(
  defineProps<{
    /**
     * When true, renders the red textured container (new channel
     * layout path). When false (default), the legacy styling is preserved
     * byte-for-byte so the flag-off path is completely unchanged.
     */
    newLayout?: boolean;
  }>(),
  { newLayout: false },
);

const ChannelLeaderboardDocument = gql(/* GraphQL */ `
  query channelLeaderboard(
    $channel_id: String!
    $leaderboard_length: LeaderboardLength
    $community_id: String
    $highlights: Boolean
    $start_date: DateTimeISO
    $end_date: DateTimeISO
    $page: Float
    $limit: Float
  ) {
    channelLeaderboard(
      channel_id: $channel_id
      leaderboard_length: $leaderboard_length
      community_id: $community_id
      highlights: $highlights
      start_date: $start_date
      end_date: $end_date
      page: $page
      limit: $limit
    ) {
      ... on Leaderboard {
        ranking {
          ...ContestLeaderboardUser
        }
        total_contestants
      }
    }
  }
`);

const channelStore = useChannelStore();
const themeStore = useThemeStore();
const userStore = useUserStore();
const activeTab = ref(LeaderboardLength.Weekly);
const isFullLeaderboardModalOpen = ref(false);

/**
 * Whether a leaderboard prize is set for the active period, reported
 * by the always-mounted ChannelMonthlyPrize child. Drives the prize-banner vs
 * pre-prize fallback in the new-layout header.
 */
const hasPrize = ref(false);

const {
  data: leaderboard,
  fetching,
  executeQuery: refetch,
} = useQuery({
  query: ChannelLeaderboardDocument,
  variables: computed(() => ({
    channel_id: channelStore.channelSlug!,
    leaderboard_length:
      activeTab.value === LeaderboardLength.Monthly
        ? LeaderboardLength.Monthly
        : LeaderboardLength.Weekly,
    highlights: true,
    community_id:
      themeStore.currentTheme?.kind === "community"
        ? themeStore.currentTheme?.id
        : undefined,
  })),
  pause: computed(() => !channelStore.channelSlug),
});

const ranking = computed(() => {
  const result =
    (leaderboard.value?.channelLeaderboard.__typename === "Leaderboard" &&
      leaderboard.value.channelLeaderboard.ranking) ||
    [];
  return result.map((r) => r as ContestLeaderboardUser);
});

const loyaltyNotifications = useLoyaltyNotifications();

watch(
  () => loyaltyNotifications.notifications.length,
  (newLength, oldLength) => {
    if (newLength > oldLength) refetch();
  },
);

watch(
  () => channelStore.lastLeaderboardUpdate,
  (newDate, oldDate) => {
    if ((newDate && !oldDate) || (newDate && oldDate && newDate > oldDate)) {
      refetch();
    }
  },
);

const communityOrChannelLogo = computed(() => {
  if (themeStore.currentTheme?.kind === AppThemeKind.community) {
    return (
      channelStore.channelCommunities?.find(
        (cc) => cc.id === themeStore.currentTheme?.id,
      )?.image ?? undefined
    );
  }
  return channelStore.channel?.logo ?? undefined;
});

const loading = computed(() => fetching.value || !channelStore.channel);
</script>

<style scoped>
/**
 * FANS LEADERBOARD feature band (new layout only).
 *
 * Centered: the WHOLE band — red textured fill included — is constrained to the
 * same centered content-column width as the QUESTS / contests section above it
 * and is horizontally centered (`mx-auto` in the template + the cap below). It
 * does NOT touch the viewport / page-region edges in either the chat-closed or
 * chat-open layout.
 *
 * The cap is set EXPLICITLY here (rather than relying solely on the
 * `.channel-page-view` parent) so the OUTER band itself — not just the inner
 * white panel — never bleeds edge-to-edge. `--channel-page-max-width` is the
 * page content-column cap (`appBreakpoints.desktop`) published on the
 * `.channel-page-region` ancestor in channel-layout-v2.vue and inherited down
 * here; the `100%` fallback keeps the band full-width-of-its-column on any
 * surface that does not provide the variable (e.g. the unit-test mount). This is
 * the same max-width + centering the sibling sections sit within, so the panel
 * aligns with them. The textured brand-colored band treatment is preserved.
 */
.leaderboard-band {
  max-width: var(--channel-page-max-width, 100%);
  padding-block: 2.5rem;

  /*
   * Brand-colour base fill. Token-driven via `--fill-brand` so the band adapts
   * to each channel's theme colour (never a hardcoded red).
   */
  background-color: var(--fill-brand);
}

/**
 * Diagonal textured sheen layered over the brand fill. Two token-free,
 * brand-neutral white overlays (a soft diagonal highlight + a fine repeating
 * stripe) read as the designed feature-panel texture rather than a flat box.
 * They sit ON TOP of `--fill-brand` so the hue still comes from the theme.
 */
.leaderboard-band-texture {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image:
    linear-gradient(
      125deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0) 55%,
      rgba(255, 255, 255, 0.12) 78%,
      rgba(255, 255, 255, 0) 100%
    ),
    repeating-linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.05) 0px,
      rgba(255, 255, 255, 0.05) 1px,
      transparent 1px,
      transparent 14px
    );
}
</style>
